import {
  computed,
  defineComponent,
  isRef,
  ref,
  unref,
  watch,
  type MaybeRef,
} from "vue";
import type { UseForm } from "../useForm";
import type { FormLayoutDefinition, FormLayoutNode } from "../layout";
import type {
  FormFieldReference,
  FormFieldReferenceAttributes,
} from "../field";
import Bubbletip from "components/bubbletip";
import InfoIcon from "assets/img/svg/icons/info-circle.svg?url";
import Icon from "components/icon";
import WithBubbletip from "components/with-bubbletip";
import type { FunctionalComponent, VNode } from "vue";
import { condcat } from "@/utils/string";
import { getComponent } from "./getComponent";
import { Grid } from "components/grid";
import type { CSSProperties } from "vue";
import { computedWithControl } from "@vueuse/core";
import { FormScope } from "../scope";
import { isFunction } from "@/utils/is";
import { CollapsedSection } from "components/collapsed-section";

export const FieldWrapper = defineComponent<{
  attributes: MaybeRef<Partial<FormFieldReferenceAttributes<any>>>;
}>(
  (props, { slots }) => {
    const attribs = computed(() => unref(props.attributes));
    const fieldLabel = computed(() => {
      if (isFunction(attribs.value.label)) return <attribs.value.label />;
      const text = attribs.value.label;
      if (!text) return text;
      if (attribs.value.required) return `${text} *`;
      return text;
    });

    const bottomLabel = computed(() => {
      if (isFunction(attribs.value.bottomLabel))
        return <attribs.value.bottomLabel />;
      const text = attribs.value.bottomLabel;
      if (!text) return text;
      return text;
    });

    const node = ref<VNode>();

    const isSelect = computed(
      () =>
        attribs.value.component === "select" ||
        attribs.value.component === "select",
    );
    const isButton = computed(
      () =>
        attribs.value.component === "button" ||
        attribs.value.component === "button",
    );

    const labelClazz = computed(() =>
      condcat(
        [
          [!attribs.value.inlineLabel, "block"],
          [
            !attribs.value.labelClass,
            "text-sm font-medium text-gray-900 dark:text-white",
          ],
          [!!attribs.value.labelClass, attribs.value.labelClass],
        ],
        " ",
      ),
    );
    const wrapClazz = computed(() =>
      condcat(
        [
          [!attribs.value.inlineLabel, "flex flex-row items-center mb-2 gap-1"],
          [attribs.value.inlineLabel, "flex items-center"],
        ],
        " ",
      ),
    );

    const Label = computed(() => (
      <div style={{ userSelect: "none" }} class={wrapClazz.value}>
        <label
          for={
            attribs.value.id ||
            attribs.value.name ||
            (typeof fieldLabel.value === "string" ? fieldLabel.value : "")
          }
          class={labelClazz.value}
        >
          {/*isFunction(props.label) ? <props.label /> : props.label */}
          {fieldLabel.value}
        </label>
        {attribs.value.tooltip && (
          <WithBubbletip
            tooltip={{
              zIndex: 500,
              body: () => {
                return (
                  <div
                    class="text-gray-500"
                    style={{
                      maxWidth: "400px",
                      overflowWrap: "break-word",
                      whiteSpace: "wrap",
                      height: "auto",
                    }}
                  >
                    {attribs.value.tooltip}
                  </div>
                );
              },
              variant: "white",
            }}
          >
            <Icon
              src={InfoIcon}
              size={14}
              fill="#6B7280"
              style={{ marginLeft: "0.3333rem" }}
            />
          </WithBubbletip>
        )}
      </div>
    ));

    const LabelWithInput = computed(() => (
      <div
        style={[
          {
            width: "100%",
          },
          attribs.value.inlineLabel
            ? ({
                height: "fit-content",
                display: "grid",
                gridTemplateColumns: "fit-content(100%) auto",
                gap: "8px",
                alignItems: "center",
              } as CSSProperties)
            : {},
        ]}
      >
        {fieldLabel.value &&
          attribs.value.labelPlacement !== "right" &&
          Label.value}

        {attribs.value.locked &&
        (attribs.value.lockedReason || attribs.value.lockedReasonBubbletip) ? (
          <WithBubbletip
            class="w-fit"
            tooltip={
              attribs.value.lockedReasonBubbletip
                ? {
                    ...attribs.value.lockedReasonBubbletip,
                    zIndex: attribs.value.lockedReasonBubbletip.zIndex ?? 500,
                  }
                : {
                    text: attribs.value.lockedReason,
                    placement: "auto",
                    variant: "white",
                    size: "s",
                    zIndex: 500,
                  }
            }
          >
            {slots.default ? slots.default() : ""}
          </WithBubbletip>
        ) : slots.default ? (
          slots.default()
        ) : (
          ""
        )}
        {fieldLabel.value &&
          attribs.value.labelPlacement === "right" &&
          Label.value}
      </div>
    ));

    const Wrapper = computed((): FunctionalComponent<any> => {
      if (attribs.value.grid)
        return (_, ctx) => {
          return (
            <Grid {...attribs.value.grid}>
              {ctx.slots.default ? ctx.slots.default() : ""}
            </Grid>
          );
        };

      return (innerProps, ctx) => (
        <div
          {...innerProps}
          ref={node}
          class={[
            "flex flex-col",
            !isButton.value &&
              !isSelect.value &&
              !attribs.value.inlineLabel &&
              "justify-end",
          ]}
          style={[
            { position: "relative" },
            attribs.value.hidden
              ? {
                  display: "none",
                  opacity: "0%",
                  position: "fixed",
                  top: 0,
                  left: 0,
                }
              : {},
          ]}
        >
          {ctx.slots.default ? ctx.slots.default() : ""}
        </div>
      );
    });

    return () => {
      return (
        <Wrapper.value>
          {/* props.before && <props.before/> */}
          {LabelWithInput.value}
          {attribs.value.subLabel && (
            <p class="text-gray-500 text-sm mt-2">{attribs.value.subLabel}</p>
          )}

          {bottomLabel.value && (
            <div class="text-gray-500 text-sm">{bottomLabel.value}</div>
          )}
          {/* props.after && <props.after/> */}
        </Wrapper.value>
      );
    };
  },
  { props: ["attributes"] },
);

const FieldLabel = defineComponent<{
  label: string | FunctionalComponent<any>;
  id?: string;
  name?: string;
  tooltip?: string;
}>(
  (props) => {
    return () => {
      return (
        <div
          style={{ userSelect: "none" }}
          class={["flex flex-row items-center mb-2 gap-1"]}
        >
          <label
            for={(props.id || props.name || props.label) + ""}
            class={["text-sm font-medium text-gray-900 dark:text-white"]}
          >
            {/*isFunction(props.label) ? <props.label /> : props.label */}
            {typeof props.label === "string" ? props.label : <props.label />}
          </label>
          {props.tooltip && (
            <WithBubbletip
              tooltip={{
                zIndex: 500,
                body: () => {
                  return (
                    <div
                      class="text-gray-500"
                      style={{
                        maxWidth: "400px",
                        overflowWrap: "break-word",
                        whiteSpace: "wrap",
                        height: "auto",
                      }}
                    >
                      {props.tooltip}
                    </div>
                  );
                },
                variant: "white",
              }}
            >
              <Icon
                src={InfoIcon}
                size={14}
                fill="#6B7280"
                style={{ marginLeft: "0.3333rem" }}
              />
            </WithBubbletip>
          )}
        </div>
      );
    };
  },
  { props: ["label", "id", "name", "tooltip"] },
);

export const FormFieldWrapper = defineComponent<{
  field: FormFieldReference<any>;
  depth?: number;
  index?: number;
  form: UseForm<any, any>;
}>(
  (props) => {
    FormScope.currentForm = props.form;

    const FieldComponent = computed(() =>
      getComponent(
        props.field.attributes.value.component ||
          props.field.attributes.value.type,
        props.form.props.components,
      ),
    );

    const injectedProps = computed(
      () => props.field.attributes.value.injectedProps,
    );
    const elRef = ref();
    const errors = computed(() =>
      (props.field.attributes.value.errors || []).filter(
        (it) => it.disabled !== true,
      ),
    );
    const error = computed(() =>
      errors.value.length > 0 ? errors.value[0] : null,
    );

    return () => {
      if (props.field.isGroup && props.field.children) {
        return (
          <div
            class={[
              props.field.attributes.value.label
                ? "grid grid-rows-[2rem_auto]"
                : "",
            ]}
          >
            {props.field.attributes.value.label && (
              <FieldLabel
                label={props.field.attributes.value.label}
                tooltip={props.field.attributes.value.tooltip}
                id={props.field.attributes.value.id || props.field.path}
                name={props.field.attributes.value.name || props.field.name}
              />
            )}
            <div
              class={[
                props.field.attributes.value.class || "",
                "relative items-end",
              ]}
            >
              {props.field.children
                .filter((it) => it.attributes.value.hidden !== true)
                .map((child, i) => {
                  return (
                    <FormFieldWrapper
                      form={props.form}
                      depth={(props.depth || 0) + 1}
                      index={i}
                      field={child}
                    />
                  );
                })}
            </div>
          </div>
        );
      }

      return (
        <div class={[props.field.attributes.value.class || "", "relative"]}>
          <FieldWrapper attributes={props.field.attributes}>
            <FieldComponent.value
              {...props.field.props}
              value={props.field.value.value}
              ref={elRef}
              options={props.field.attributes.value.options}
              locked={
                props.field.attributes.value.locked ||
                props.field.attributes.value.disabled
              }
              lockedReasonBubbletip={
                props.field.attributes.value.lockedReasonBubbletip
              }
              disabled={
                props.field.attributes.value.locked ||
                props.field.attributes.value.disabled
              }
              placeholder={props.field.attributes.value.placeholder}
              display={props.field.attributes.value.display}
              {...(injectedProps.value || {})}
            />
            {error.value && (
              <Bubbletip
                triggerRef={elRef}
                noMouseEvents
                variant="white"
                visible
                text={error.value.message}
                {...(props.field.attributes.value.errorProps || {})}
              />
            )}
          </FieldWrapper>
        </div>
      );
    };
  },
  { props: ["field", "depth", "index", "form"] },
);

export const FormLayout = defineComponent<{
  form: UseForm<any, any>;
  layout: MaybeRef<FormLayoutNode<any, any>>;
  depth?: number;
  index?: number;
}>(
  (props) => {
    FormScope.currentForm = props.form;

    const layout = computed(() => unref(props.layout));
    const layoutChildren = computed(() =>
      (layout.value.children.value || []).filter((it) => !unref(it.hidden)),
    );
    const depth = props.depth || 0;
    const field = computed(() =>
      layout.value.render ? props.form.getField(layout.value.render) : null,
    );

    const LayoutComponent = computedWithControl(
      [
        (() => unref(layout.value.component)) as any,
        layout.value.version as any,
      ],
      () => {
        return unref(layout.value.component);
      },
    );

    const hiddenChildren = computed(() => {
      return layoutChildren.value.map((childLay) => {
        const child = unref(childLay);
        const childField = child.render
          ? props.form.getField(child.render)
          : null;
        const attribs = childField ? unref(childField.attributes) : null;
        return !!attribs?.hidden;
      });
    });

    const innerContent = computed(() => {
      return (
        <div class={layout.value.attributes?.wrapperClass || "w-full"}>
          {
            layout.value.attributes?.label && <FieldLabel label={layout.value.attributes.label}/>
          }
          {field.value && (
            <FormFieldWrapper field={field.value} form={props.form} />
          )}
          {LayoutComponent.value && (
            <LayoutComponent.value
              state={props.form.state}
              data={props.form.computedData}
            />
          )}
          {layoutChildren.value.length > 0 && (
            <div
              class={
                layout.value.attributes?.classOverride || [
                  layout.value.attributes?.class || "grid grid-auto-rows gap-6",
                  "w-full items-end",
                ]
              }
            >
              {layoutChildren.value
                .filter((_child, i) => !hiddenChildren.value[i])
                .map((child, i) => {
                  return (
                    <FormLayout
                      form={props.form}
                      layout={child}
                      index={i}
                      depth={depth + 1}
                    />
                  );
                })}
            </div>
          )}
        </div>
      );
    });

    const title = computed(() => layout.value.title || layout.value.name);
    const description = computed(() => layout.value.description);

    return () => {
      return layout.value.collapsible && title.value ? (
        <CollapsedSection title={title.value} description={description}>
          {innerContent.value}
        </CollapsedSection>
      ) : (
        innerContent.value
      );
    };
  },
  { props: ["form", "layout", "depth", "index"] },
);

export const Form = defineComponent<{
  form: UseForm<any, any>;
  layout?: MaybeRef<FormLayoutDefinition<any, any>>;
}>(
  (props) => {
    FormScope.currentForm = props.form;

    if (props.layout) {
      if (isRef(props.layout)) {
        watch(
          props.layout,
          (layout) => {
            props.form.setLayout(layout);
          },
          { immediate: true, deep: true },
        );
      } else {
        props.form.setLayout(props.layout);
      }
    }

    return () => {
      return (
        <div class="w-full">
          <FormLayout form={props.form} layout={props.form.layout} />
        </div>
      );
    };
  },
  { props: ["form", "layout"] },
);
